/**
 * Templates API — wraps `/v1/templates/*` (W3 backend).
 */
import { apiClient, ApiError } from "./client";

export type TemplateChannel = "telegram" | "max" | "email" | "sms" | "push" | "webhook";

export type TelegramParseMode = "HTML" | "MarkdownV2" | "Markdown";

export interface TelegramButton {
  text: string;
  /** Outbound URL. Mutually exclusive with callback_data. */
  url?: string;
  callback_data?: string;
}

/** Telegram-specific fields stored in template.content (text lives in body). */
export interface TelegramContent {
  parse_mode?: TelegramParseMode;
  /** Inline-keyboard rows. */
  buttons?: TelegramButton[][];
  disable_web_page_preview?: boolean;
}

export interface Template {
  id: string;
  tenant_id: string;
  name: string;
  channel_type: TemplateChannel;
  subject: string | null;
  body: string;
  variables: Record<string, unknown>;
  /** Per-channel envelope (telegram: parse_mode + inline-keyboard buttons). */
  content: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface TemplateList {
  rows: Template[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface TemplateUpsertInput {
  name: string;
  channel_type?: TemplateChannel;
  subject?: string | null;
  body: string;
  variables?: Record<string, unknown>;
  content?: Record<string, unknown>;
}

export interface TemplatePreviewInput {
  vars?: Record<string, unknown>;
  render_subject?: boolean;
}

export interface InlinePreviewInput {
  body: string;
  subject?: string | null;
  vars?: Record<string, unknown>;
}

export interface TemplatePreviewResult {
  rendered: string;
  subject: string | null;
  missing_vars: string[];
  connected_content_calls: Array<{ url: string; ms: number; cached: boolean; error?: string }>;
}

export const templatesApi = {
  async list(params: { limit?: number; offset?: number; summary?: boolean } = {}): Promise<TemplateList> {
    const res = await apiClient.get<TemplateList>("/v1/templates", { params });
    return res.data;
  },

  async get(id: string): Promise<Template> {
    const res = await apiClient.get<Template>(`/v1/templates/${id}`);
    return res.data;
  },

  async create(input: TemplateUpsertInput): Promise<Template> {
    const res = await apiClient.post<Template>("/v1/templates", input);
    return res.data;
  },

  async update(id: string, input: Partial<TemplateUpsertInput>): Promise<Template> {
    const res = await apiClient.patch<Template>(`/v1/templates/${id}`, input);
    return res.data;
  },

  async archive(id: string): Promise<void> {
    await apiClient.delete(`/v1/templates/${id}`);
  },

  /** Clone a template (content + body + subject) into a new "… (копия)" so the
   *  original is never touched. Auto-suffixes the name on a collision. */
  async duplicate(id: string): Promise<Template> {
    const src = await this.get(id);
    const base = {
      channel_type: src.channel_type,
      subject: src.subject ?? undefined,
      body: src.body,
      variables: src.variables ?? undefined,
      content: src.content ?? undefined,
    };
    for (let i = 1; i <= 50; i++) {
      const name = i === 1 ? `${src.name} (копия)` : `${src.name} (копия ${i})`;
      try {
        return await this.create({ ...base, name });
      } catch (e) {
        if (e instanceof ApiError && (e.status === 409 || e.code === "TEMPLATE_NAME_EXISTS")) continue;
        throw e;
      }
    }
    throw new Error("Слишком много копий — переименуйте оригинал");
  },

  async preview(id: string, input: TemplatePreviewInput = {}): Promise<TemplatePreviewResult> {
    const res = await apiClient.post<TemplatePreviewResult>(`/v1/templates/${id}/preview`, input);
    return res.data;
  },

  async previewInline(input: InlinePreviewInput): Promise<TemplatePreviewResult> {
    const res = await apiClient.post<TemplatePreviewResult>("/v1/templates/preview", input);
    return res.data;
  },
};
